import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { get, isEmpty } from 'lodash';
import { ApiService, CommonRegEx, SitesService } from '@nrwl/common-lib';
import { Subject } from 'rxjs';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'subnet-settings',
  templateUrl: './subnet.settings.template.html',
  styleUrls: ['./subnet.settings.style.scss']
})
export class SubnetSettingsComponent implements OnInit, OnChanges {
  @Input() parentOutput: any; // eslint-disable-line
  @Input() parent: string;
  @Input() step: any; // eslint-disable-line
  @Input() validateUrl: string;
  @Input() key: string;

  isLoading = false;
  dataLocation: any; // eslint-disable-line
  subnetList: any; // eslint-disable-line
  tagList: any; // eslint-disable-line
  subnetForm: UntypedFormGroup;
  validSubnet = false;
  errorMsg: string;
  checkingSubnet = false;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly apiService: ApiService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly sitesService: SitesService
  ) {
    this.subnetList = [];
    this.tagList = [];
  }

  ngOnInit() {
    if (this.parent === 'subnets') {
      this.subnetForm = this.formBuilder.group({
        name: new UntypedFormControl(''),
        subnet: new UntypedFormControl(
          {
            value: '',
            disabled: false
          },
          [this.validateSubnet.bind(this)]
        ),
        description: new UntypedFormControl('', [])
      });
    } else if (this.parent === 'tags') {
      this.subnetForm = new UntypedFormGroup({
        name: new UntypedFormControl('')
      });
    }

    this.resetSelectedItem();
  }

  /**
   * Please note this event is triggered only for one-way bindings (<)
   */
  ngOnChanges() {
    this.resetSelectedItem();
  }

  resetOutput(): void {
    this.parentOutput.subnets = null;
    this.resetSelectedItem();
  }

  public makeOutput(): void {
    this.subnetList = this.subnetList?.length > 0 ? this.subnetList : [];
    if (this.parent === 'subnets' && this.validSubnet) {
      const subnet = {
        name: this.subnetForm.value.name,
        description: this.subnetForm.value.description,
        subnet: this.subnetForm.value.subnet
      };
      this.subnetList.push(subnet);
      this.parentOutput.subnets = this.subnetList;
      this.errorMsg = '';
    } else if (this.parent === 'tags') {
      const tag = get(this.subnetForm.value, 'name', '');

      if (isEmpty(tag)) {
        return;
      }
      this.tagList.push(tag);
      this.parentOutput.tags = this.tagList;
    }
    this.subnetForm.reset();
  }

  public checkSubnetsWithOtherSites(): void {
    const subnet = {
      name: this.subnetForm.value.name,
      description: this.subnetForm.value.description,
      subnet: this.subnetForm.value.subnet
    };
    this.checkingSubnet = true;
    this.apiService
      .post$(this.validateUrl, subnet)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.checkingSubnet = false;
          if (response === null) {
            this.makeOutput();
            this.validSubnet = true;
            this.errorMsg = '';
          } else if (response.error.status === 500) {
            this.validSubnet = false;
            this.errorMsg = response.error.message;
          }
        },
        err => {
          this.checkingSubnet = false;
          if (err.status === 500) {
            const siteId = get(this.step.parent, 'defaultParamsById.id', '');
            this.errorMsg = 'subnet overlaps with another site';
            this.validSubnet = false;
            if (err && err.error && err.error.message) {
              const regExp = /\[([^)]+)\]/;
              const matches = regExp.exec(err.error.message);
              if (matches && matches.length > 1) {
                try {
                  // many subnets can come back. we only need the first one
                  const overlappedSubnet = matches[1].split(',')[0];
                  const msite = this.sitesService.getSiteByIP(overlappedSubnet.split('/')[0]);
                  if (msite && msite.id === siteId) {
                    this.validSubnet = true;
                    this.errorMsg = '';
                    this.makeOutput();
                  } else {
                    if (msite && msite.name) {
                      this.errorMsg = `subnet overlaps with site: ${msite.name} subnet: ${overlappedSubnet}`;
                    }
                  }
                } catch (err) {}
              }
            }
          }
        },
        () => {
          this.checkingSubnet = false;
        }
      );
  }

  // eslint-disable-next-line
  public removeSubnet(item: any): void {
    if (this.parent === 'subnets') {
      const index: number = this.subnetList.indexOf(item);
      if (index !== -1) {
        this.subnetList.splice(index, 1);
      }
    } else if (this.parent === 'tags') {
      const index: number = this.tagList.indexOf(item);
      if (index !== -1) {
        this.tagList.splice(index, 1);
      }
    }
  }

  public subnetUpdated(): void {
    this.errorMsg = '';
  }

  // eslint-disable-next-line
  private validateSubnet(control: AbstractControl): any {
    if (control.value !== undefined && control.value !== null) {
      if (
        CommonRegEx.IP_V4_CIDR.test(control.value) ||
        CommonRegEx.IP_V6_CIDR.test(control.value)
      ) {
        this.validSubnet = true;
        return null;
      } else {
        this.validSubnet = false;
        return { matchPattern: true };
      }
    } else {
      this.validSubnet = false;
      return { matchPattern: true };
    }
  }

  private resetSelectedItem(): void {
    if (this.parent === 'subnets') {
      this.subnetList = this.parentOutput.subnets;
    } else if (this.parent === 'tags' && this.parentOutput.tags !== null) {
      this.tagList = this.parentOutput.tags;
    }
  }

  // eslint-disable-next-line
  get f(): any {
    return this.subnetForm.controls;
  }
}
